<template>
	<div
		class="form-input"
		:class="{
			'form-input--icon': icon,
			'form-input--disabled': disabled,
			'form-input--focused': focused,
			'form-input--error': error,
			'form-input--success': success,
			'form-input--not-empty': value != '' && value !== undefined,
			'form-input--suggestions-open': isPopoverOpen
		}"
	>
		<div class="form-input__wrapper">
			<input
				ref="input"
				:type="type"
				:id="id"
				:name="name"
				class="form-input__field"
				:class="{
					'form-input__field--valid': !error,
					'form-input__field--invalid': error,
					'form-input__field--empty': value == '',
					'form-input__field--password': type == 'password',
				}"
				:placeholder="placeholder"
				:required="required"
				:disabled="disabled"
				:readonly="readonly"
				:minlength="minlength"
				:maxlength="maxlength"
				:inputmode="inputmode"
				:pattern="pattern"
				:value="value"
				v-bind="attributes"
				@input="onInput"
				@focus="onFocus"
				@blur="onBlur"
				@keyup="onKeyup"
				@keydown="onKeydown"
			/>
			<label
				:for="id"
				class="form-input__label | form-input-label"
				v-if="label"
			>
				<span
					class="form-input-label__text"
					v-html="label"
				/>
				<span
					class="form-input-label__option"
					v-if="labelOption"
					v-html="labelOption"
				/>
			</label>
			<component
				class="form-input__icon"
				v-if="icon"
				:is="iconComponent(icon)"
			/>
			<ul
				:id="`${id}-list`"
				ref="popover"
				class="form-input__suggestions"
				tabindex="-1"
				role="listbox"
			>
				<li
					v-for="(suggestion, index) in filteredSuggestions"
					:id="`${id}-suggestion-${index}`"
					:key="index"
					ref="suggestion"
					v-html="suggestion.label"
					role="option"
					:class="['form-input__suggestion', {
						'form-input__suggestion--hover': isPopoverOpen && focusedValue === suggestion.label
					}]"
					@mouseenter="onSuggestionHover(suggestion)"
					@click.prevent.stop="onSuggestionClick(suggestion)"
				/>
			</ul>
		</div>
		<span
			class="form-input__hint | form-input-hint"
			v-if="hint"
			v-html="hint"
		/>
		<span
			class="form-input__error | form-input-error"
			v-if="error && errorMessage"
			v-html="errorMessage"
		/>
		<span
			class="form-input__success | form-input-success"
			v-if="successMessage"
			v-html="successMessage"
		/>
	</div>
</template>

<script>
export default {
	props: {
		id: {
			type: String,
			default() {
				return Date.now().toString(36) + Math.random().toString(36).substring(2, 7)
			}
		},
		name: String,
		value: Number | String,
		type: {
			type: String,
			default() {
				return 'text';
			},
		},
		placeholder: String,
		required: Boolean,
		disabled: Boolean,
		readonly: Boolean,
		maxlength: Number,
		minlength: Number,
		inputmode: String,
		pattern: String,
		label: String,
		labelOption: String,
		icon: String,
		attributes: Object,
		hint: String,
		error: Boolean,
		errorMessage: String,
		success: Boolean,
		successMessage: String,
		suggestions: Array
	},
	data() {
		return {
			focused: false,
			focusedValue: '',
			isPopoverOpen: false,
			focusTimeout: null,

		}
	},
	computed: {
		filteredSuggestions() {
			const query = this.value.toLowerCase()

			return this.suggestions?.filter(suggestion => {
				const suggestionText = suggestion.label
				return suggestionText.toLowerCase().includes(query) && suggestionText !== this.value
			}) ?? []
		}
	},
	model: {
		prop: 'value',
		event: 'input'
	},
	watch: {
		suggestions() {
			this.focused && this.showPopover()
		}
	},
	methods: {
		async showPopover() {
			if (this.filteredSuggestions.length && !this.isPopoverOpen) {
				this.isPopoverOpen = true
				await this.$nextTick()
				this.scrollToSuggestion(this.focusedValue)
			}
		},
		async hidePopover() {
			if (this.isPopoverOpen) {
				this.focusedValue = ''
				this.isPopoverOpen = false
			}
		},

		scrollToSuggestion(value) {
			if (this.isPopoverOpen) {
				const index = this.filteredSuggestions.findIndex(suggestion => suggestion.label === value)
				const suggestion = this.$refs.suggestion?.at(index)

				console.log(value, this.filteredSuggestions.map(s => s.label))

				if (suggestion) {
					suggestion.scrollIntoView({ block: 'nearest' })
				}
			}
		},
		onSuggestionHover(suggestion) {
			this.focusedValue = suggestion.label
		},
		onSuggestionClick(suggestion) {
			clearTimeout(this.focusTimeout)
			this.$emit('suggestion:selected', suggestion)
			this.hidePopover()
		},

		focus() {
			this.$refs.input.focus();
		},
		onInput(e) {
			this.$emit('input', e.target.value);
		},
		onFocus(e) {
			this.focused = true;
			this.showPopover()
			this.$emit('focus', e);
		},
		onBlur(e) {
			this.focused = false;
			this.focusTimeout = setTimeout(() => {
				this.hidePopover()
			}, 200)
			this.$emit('blur', e);
		},
		onKeyup(e) {
			this.$emit('keyup', e);
		},
		async onKeydown(e) {
			this.$emit('keydown', e);

			if (e.key !== 'Tab') {
				if (e.key === 'Escape') {
					this.hidePopover()
					return
				}

				if (e.key === 'Enter') {
					if (this.isPopoverOpen && this.focusedValue) {
						e.preventDefault()
						this.$emit('suggestion:selected', this.filteredSuggestions.find(suggestion => suggestion.label === this.focusedValue))
						this.hidePopover()
					}

					return
				}

				if (!this.isPopoverOpen) {
					this.showPopover()
					return
				}
			}

			if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
				if (!this.filteredSuggestions.length) {
					this.focusedValue = ''
				}

				e.preventDefault()

				let addend = -1

				if (e.key === 'ArrowDown') {
					addend = 1
				}

				const currentIndex = this.filteredSuggestions.findIndex(suggestion => suggestion.label === this.focusedValue)
				const maxIndex = this.filteredSuggestions?.length - 1
				let newIndex = Math.min(Math.max(0, currentIndex + addend), maxIndex)
				let newsuggestion = this.filteredSuggestions[newIndex]
				this.focusedValue = newsuggestion.label ?? this.focusedValue

				await this.$nextTick()
				this.scrollToSuggestion(this.focusedValue)
			}





		},
	},
	mounted() {

	}
}
</script>
